/**
 * Pure helpers for Settings, Policies: how a rule reads in one line, the order rules are
 * listed in (the order precedence applies them), and the editor's form <-> rule input.
 */
import type { Id, PolicyDecision, PolicyRule, PolicyRuleInput, PolicySimulationResult } from '@orgus/protocol';

export const DECISION_LABEL: Record<PolicyDecision, string> = { allow: 'Allow', ask: 'Ask a person', deny: 'Deny' };

/** "Bash `npm test:*`", "session.start with model opus*", "Any tool". */
export function describeMatch(m: PolicyRule['match']): string {
  const parts: string[] = [];
  if (m.action) parts.push(m.action);
  if (m.tool) parts.push(m.tool === '*' ? 'Any tool' : m.tool);
  if (m.pattern) parts.push(`matching ${m.pattern}`);
  if (m.model) parts.push(`with model ${m.model}`);
  if (!parts.length) return 'Anything';
  if (!m.action && !m.tool) parts.unshift('Any tool');
  return parts.join(' ');
}

const RANK: Record<PolicyDecision, number> = { deny: 0, ask: 1, allow: 2 };

/**
 * Project rules first (they win over org rules), then org rules; inside a scope deny, ask,
 * allow (deny beats ask beats allow); then newest first.
 */
export function sortRules(rules: readonly PolicyRule[], projectName: (id: Id) => string | undefined = () => undefined): PolicyRule[] {
  const scopeKey = (r: PolicyRule) => (r.scope.kind === 'project' ? `0${projectName(r.scope.projectId) ?? r.scope.projectId}` : '1');
  return [...rules].sort((a, b) => scopeKey(a).localeCompare(scopeKey(b)) || RANK[a.decision] - RANK[b.decision] || b.createdAt - a.createdAt);
}

export function scopeLabel(r: Pick<PolicyRule, 'scope'>, projectName: (id: Id) => string | undefined): string {
  return r.scope.kind === 'org' ? 'Every project' : (projectName(r.scope.projectId) ?? 'Unknown project');
}

/** The editor's fields. Empty strings mean "not set". `scope` is 'org' or a project id. */
export interface RuleForm {
  scope: string;
  kind: 'tool' | 'action';
  tool: string;
  action: string;
  pattern: string;
  model: string;
  decision: PolicyDecision;
  reason: string;
  enabled: boolean;
}

export function emptyForm(): RuleForm {
  return { scope: 'org', kind: 'tool', tool: 'Bash', action: '', pattern: '', model: '', decision: 'allow', reason: '', enabled: true };
}

export function formOf(r: PolicyRule): RuleForm {
  return {
    scope: r.scope.kind === 'org' ? 'org' : r.scope.projectId,
    kind: r.match.action ? 'action' : 'tool',
    tool: r.match.tool ?? '',
    action: r.match.action ?? '',
    pattern: r.match.pattern ?? '',
    model: r.match.model ?? '',
    decision: r.decision,
    reason: r.reason,
    enabled: r.enabled !== false,
  };
}

/** The rule input, or what is missing in plain words. */
export function inputOf(f: RuleForm): { ok: true; input: PolicyRuleInput } | { ok: false; error: string } {
  const tool = f.kind === 'tool' ? f.tool.trim() : '';
  const action = f.kind === 'action' ? f.action.trim() : '';
  const pattern = f.pattern.trim();
  const model = f.model.trim();
  if (f.kind === 'action' && !action) return { ok: false, error: 'Pick the Orgus action this rule applies to.' };
  if (f.kind === 'tool' && !tool && !pattern) return { ok: false, error: 'Name a tool or a pattern, or the rule would match every request.' };
  const match: PolicyRule['match'] = {};
  if (tool) match.tool = tool;
  if (action) match.action = action as NonNullable<PolicyRule['match']['action']>;
  if (pattern) match.pattern = pattern;
  if (model) match.model = model;
  return {
    ok: true,
    input: {
      scope: f.scope === 'org' ? { kind: 'org' } : { kind: 'project', projectId: f.scope },
      match,
      decision: f.decision,
      ...(f.reason.trim() ? { reason: f.reason.trim() } : {}),
      enabled: f.enabled,
    },
  };
}

/** One sentence for a simulation: what would change. */
export function summarizeSimulation(r: PolicySimulationResult): string {
  if (!r.total) return 'No requests from launched sessions in the last 7 days to replay.';
  if (!r.changedCount) return `None of the ${r.total} requests from the last 7 days would be decided differently.`;
  const moved = (['allow', 'ask', 'deny'] as const)
    .map((d) => ({ d, delta: r.after[d] - r.before[d] }))
    .filter((x) => x.delta > 0)
    .map((x) => `${x.delta} more ${x.d === 'allow' ? 'allowed' : x.d === 'deny' ? 'denied' : 'asked'}`);
  return `${r.changedCount} of ${r.total} requests from the last 7 days would be decided differently${moved.length ? ` (${moved.join(', ')})` : ''}.`;
}
